import type { CourseCertificateEvidenceModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import DocumentIcon from "@/components/icons/DocumentIcon";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";

import { DocumentPaginationControls } from "./DocumentPaginationControls";
import { StatusBadge } from "./StatusBadge";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "./utils/evidenceStatus";
import { formatDate } from "./utils/formatters";
import type { MockPagination } from "./types";

interface CourseCertificatesTableProps {
  certificates: CourseCertificateEvidenceModel[];
  isLoading: boolean;
  error: Error | null;
  onSelectCertificate: (certificateId: string) => void;
  pagination: MockPagination;
  showPagination?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export const CourseCertificatesTable = ({
  certificates,
  isLoading,
  error,
  onSelectCertificate,
  pagination,
  showPagination = true,
  onPreviousPage,
  onNextPage,
}: CourseCertificatesTableProps) => (
  <>
    <TableRoot>
      <TableHead>
        <th>Dokument</th>
        <th>Kursus</th>
        <th>Udbyder</th>
        <th>Kursusdato</th>
        <th>Lektioner</th>
        <th>Status</th>
        <th>Handling</th>
      </TableHead>
      <TableBody>
        {isLoading && (
          <tr>
            <td colSpan={7} className="text-muted">
              Henter kursusbeviser ...
            </td>
          </tr>
        )}
        {error && (
          <tr>
            <td colSpan={7}>
              <CleApiErrorText
                error={error}
                fallback="Kunne ikke hente kursusbeviser."
              />
            </td>
          </tr>
        )}
        {!isLoading &&
          !error &&
          certificates.map((certificate) => (
            <tr
              key={certificate.id}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectCertificate(certificate.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectCertificate(certificate.id);
                }
              }}
            >
              <td>
                <div className="d-flex align-items-center gap-2">
                  <DocumentIcon height={16} />
                  {certificate.fileName}
                </div>
              </td>
              <td>{certificate.courseTitle ?? "-"}</td>
              <td>{certificate.courseProvider ?? "-"}</td>
              <td>{formatDate(certificate.courseDate)}</td>
              <td>{certificate.numberOfLessons ?? "-"}</td>
              <td>
                <StatusBadge
                  status={formatEvidenceStatus(certificate.status)}
                  variant={getEvidenceStatusVariant(certificate.status)}
                />
              </td>
              <td>
                <button
                  id={`efteruddannelse-course-certificate-${certificate.id}`}
                  type="button"
                  className="btn btn--action"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectCertificate(certificate.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          ))}
        {!isLoading && !error && !certificates.length && (
          <tr>
            <td colSpan={7} className="text-muted">
              Ingen kursusbeviser
            </td>
          </tr>
        )}
      </TableBody>
    </TableRoot>
    <div className="d-flex justify-content-end mt-2">
      <DocumentPaginationControls
        pagination={pagination}
        showPagination={showPagination}
        onPreviousClick={onPreviousPage}
        onNextClick={onNextPage}
      />
    </div>
  </>
);
